import styled from "@emotion/styled";
import PropTypes from "prop-types";
import React, { useRef } from "react";
import { filter, find, isEmpty } from "lodash";

import ContentContainer from "~/components/layout/ContentContainer";
import BaseLink from "~/components/links/BaseLink";
import TopNavLinks from "~/components/Header/TopNavLinks";

import transition from "~/utilities/transition";
import { KEYCODE_SPACE } from "~/utilities/helpers";
import { topNavSubNavKeyboardNavigation } from "~/utilities/accessibilityHelpers";
import useOnKeyPress from "~/hooks/useOnKeyPress";

import { useUserLocation } from "~/context/UserLocationContext";

import "flag-icons/css/flag-icons.min.css";

const Wrap = styled.div`
    background-color: ${({ theme }) => theme.palette.black};
    min-height: 30px;
    display: none;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobileLandscape}) {
        display: block;
    }
`;

const TopNavLinksWrapper = styled.div`
    display: flex;
`;

const Country = styled.div`
    display: flex;
`;

const TopNav = styled.nav`
    display: flex;
`;

const Flag = styled.span`
    margin-right: 5px;
`;
export const TopNavLinkWrapper = styled.div`
    ${transition({}, "background-color")};
    position: relative;
    padding: 5px 10px;
    background-color: ${({ theme, isOpen }) =>
        isOpen ? `${theme.palette.lightBlue}` : `none`};

    &:hover,
    &:focus {
        background-color: ${({ theme }) => theme.palette.lightBlue};
    }
`;

const Languages = styled.div`
    ${transition({}, "background-color")};
    position: relative;
    padding: 5px 10px;
    background-color: ${({ theme, isOpen }) =>
        isOpen ? `${theme.palette.lightBlue}` : `none`};
    margin-right: auto;

    &:not(:first-of-type) {
        display: none;
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletPortrait}) {
        margin-right: 0;

        &:not(:first-of-type) {
            margin-right: auto;
            display: block;
        }
    }

    &:hover,
    &:focus {
        background-color: ${({ theme }) => theme.palette.lightBlue};
    }
`;

export const TopNavLink = styled(BaseLink)`
    font-weight: 700;
    font-size: 1.3rem;
    color: ${({ theme }) => theme.palette.white};

    &:not(:first-of-type) {
        margin-left: 10px;
    }
`;

const TopNavButton = styled.button`
    appearance: none;
    border: none;
    font-weight: 700;
    font-size: 1.3rem;
    color: ${({ theme }) => theme.palette.white};

    &:not(:first-of-type) {
        margin-left: 10px;
    }
`;

const SingleSubNavLeft = styled.div`
    ${transition({}, "opacity", "visibility")};
    opacity: ${({ isOpen }) => (isOpen ? `1` : `0`)};
    visibility: ${({ isOpen }) => (isOpen ? `visible` : `hidden`)};
    position: absolute;
    top: 100%;
    left: 0;
    padding: ${({ theme }) => `15px ${theme.layout.gutter}`};
    background-color: ${({ theme }) => theme.palette.whiteOverlay};
    display: flex;
    flex-direction: column;
    width: max-content;
    z-index: ${({ theme }) => theme.zIndex.dropdown};
`;

export const SingleSubNavRight = styled.div`
    ${transition({}, "opacity", "visibility")};
    opacity: ${({ isOpen }) => (isOpen ? `1` : `0`)};
    visibility: ${({ isOpen }) => (isOpen ? `visible` : `hidden`)};
    position: absolute;
    top: ${({ singleColumnSubNavigation }) =>
        singleColumnSubNavigation ? `57px` : `100%`};
    right: 0;
    padding: ${({ theme }) => `15px ${theme.layout.gutter}`};
    background-color: ${({ theme }) => theme.palette.whiteOverlay};
    display: flex;
    flex-direction: column;
    width: max-content;
    z-index: ${({ theme }) => theme.zIndex.subNav};
`;

export const TopSubNavLink = styled(BaseLink)`
    ${transition({}, "color")};
    color: ${({ theme }) => theme.palette.black};
    font-weight: 900;
    font-size: 1.8rem;
    padding-top: 5px;

    &:hover,
    &:focus {
        color: ${({ theme }) => theme.palette.lightBlue};
    }
`;

const TopSubNavButton = styled.button`
    appearance: none;
    border: none;
    ${transition({}, "color")};
    color: ${({ theme }) => theme.palette.black};
    font-weight: 900;
    font-size: 1.8rem;
    padding-top: 5px;

    &:hover,
    &:focus {
        color: ${({ theme }) => theme.palette.lightBlue};
    }
`;

const TopHeader = ({
    locationSiteLinks = {},
    topNav = [],
    openTopSubnav = null,
    openLangSubNav,
    openCountrySubNav,
    dispatch,
    mobileMenuIsOpen,
    languages = [],
}) => {
    const countryRef = useRef();
    const locationHasSubItems =
        locationSiteLinks.additionalSiteLocations?.length;

    const activeLanguage = find(languages, { isActive: true });
    const allOtherLanguages = filter(languages, { isActive: false });
    const languageHasSubNav = allOtherLanguages.length > 0;

    const { userLocation, setUserLocation, countries } = useUserLocation();
    const allOtherCountries = filter(
        countries,
        (country) => country.slug !== userLocation.slug,
    );

    useOnKeyPress(KEYCODE_SPACE, (keyboardEvent) => {
        if (!mobileMenuIsOpen && countryRef.current) {
            topNavSubNavKeyboardNavigation(
                keyboardEvent,
                countryRef,
                locationHasSubItems,
                openTopSubnav,
                locationSiteLinks.currentLocation,
                dispatch,
            );
        }
    });

    return (
        <Wrap>
            <ContentContainer>
                <TopNavLinksWrapper>
                    {locationSiteLinks?.currentLocation ? (
                        <Country>
                            <TopNavLinkWrapper
                                ref={countryRef}
                                isOpen={
                                    openTopSubnav ===
                                    locationSiteLinks.currentLocation
                                }
                                onMouseEnter={() =>
                                    dispatch({
                                        type: "HANDLE_MOUSE_ENTER_TOP",
                                        id: locationSiteLinks.currentLocation,
                                        hasSubitems: locationHasSubItems,
                                    })
                                }
                                onMouseLeave={() =>
                                    dispatch({
                                        type: "HANDLE_MOUSE_LEAVE",
                                        hasSubitems: locationHasSubItems,
                                    })
                                }
                            >
                                <TopNavLink to="/">
                                    {locationSiteLinks.currentLocation}
                                </TopNavLink>
                                {locationHasSubItems ? (
                                    <SingleSubNavLeft
                                        isOpen={
                                            openTopSubnav ===
                                            locationSiteLinks.currentLocation
                                        }
                                    >
                                        {locationSiteLinks.additionalSiteLocations.map(
                                            ({ linkUrl, linkLabel }, index) => (
                                                <TopSubNavLink
                                                    key={`${index}-${linkLabel}`}
                                                    to={linkUrl}
                                                >
                                                    {linkLabel}
                                                </TopSubNavLink>
                                            ),
                                        )}
                                    </SingleSubNavLeft>
                                ) : null}
                            </TopNavLinkWrapper>
                        </Country>
                    ) : null}

                    {!isEmpty(languages) ? (
                        <Languages
                            onMouseEnter={() =>
                                dispatch({
                                    type: "HANDLE_MOUSE_ENTER_LANG",
                                    id: activeLanguage.code,
                                    hasSubitems: languageHasSubNav,
                                })
                            }
                            onMouseLeave={() =>
                                dispatch({
                                    type: "HANDLE_MOUSE_LEAVE_LANG",
                                    hasSubitems: languageHasSubNav,
                                })
                            }
                            onFocus={() => {
                                dispatch({
                                    type: "HANDLE_MOUSE_ENTER_LANG",
                                    id: activeLanguage.code,
                                    hasSubitems: languageHasSubNav,
                                });
                            }}
                            onBlur={() => {
                                dispatch({
                                    type: "HANDLE_MOUSE_LEAVE_LANG",
                                    hasSubitems: languageHasSubNav,
                                });
                            }}
                        >
                            <TopNavLink to={activeLanguage.link}>
                                {activeLanguage.native_name}
                            </TopNavLink>
                            {languageHasSubNav ? (
                                <SingleSubNavLeft
                                    isOpen={
                                        openLangSubNav === activeLanguage.code
                                    }
                                >
                                    {allOtherLanguages.map(
                                        (
                                            { code, native_name, link },
                                            index,
                                        ) => (
                                            <TopSubNavLink
                                                key={`${index}-${code}`}
                                                to={link}
                                            >
                                                {native_name}
                                            </TopSubNavLink>
                                        ),
                                    )}
                                </SingleSubNavLeft>
                            ) : null}
                        </Languages>
                    ) : null}

                    {!isEmpty(countries) ? (
                        <Languages
                            onMouseEnter={() => {
                                dispatch({
                                    type: "HANDLE_MOUSE_ENTER_COUNTRY",
                                    id: userLocation,
                                    hasSubitems: true,
                                });
                            }}
                            onMouseLeave={() => {
                                dispatch({
                                    type: "HANDLE_MOUSE_LEAVE_COUNTRY",
                                    hasSubitems: true,
                                });
                            }}
                            onFocus={() => {
                                dispatch({
                                    type: "HANDLE_MOUSE_ENTER_COUNTRY",
                                    id: userLocation,
                                    hasSubitems: true,
                                });
                            }}
                            onBlur={() => {
                                dispatch({
                                    type: "HANDLE_MOUSE_LEAVE_COUNTRY",
                                    hasSubitems: true,
                                });
                            }}
                        >
                            <TopNavButton
                                onClick={() =>
                                    setUserLocation(userLocation.slug)
                                }
                            >
                                <Flag
                                    className={`fi fi-${userLocation.slug} `}
                                />
                                {userLocation.name}
                            </TopNavButton>
                            <SingleSubNavLeft isOpen={openCountrySubNav}>
                                {allOtherCountries.map(
                                    ({ name, slug }, index) => (
                                        <TopSubNavButton
                                            key={`${index}-${slug}`}
                                            onClick={() => {
                                                setUserLocation(slug);
                                                dispatch({
                                                    type: "HANDLE_MOUSE_LEAVE_COUNTRY",
                                                    hasSubitems: true,
                                                });
                                            }}
                                        >
                                            <Flag className={`fi fi-${slug}`} />
                                            {name}
                                        </TopSubNavButton>
                                    ),
                                )}
                            </SingleSubNavLeft>
                        </Languages>
                    ) : null}

                    {topNav?.length ? (
                        <TopNav title="Top Navigation">
                            {topNav.map(
                                (
                                    { linkLabel, linkUrl, subNavigationItems },
                                    index,
                                ) => (
                                    <TopNavLinks
                                        key={`${linkLabel}-${index}`}
                                        {...{
                                            linkLabel,
                                            linkUrl,
                                            subNavigationItems,
                                            index,
                                            openTopSubnav,
                                            dispatch,
                                            mobileMenuIsOpen,
                                        }}
                                    />
                                ),
                            )}
                        </TopNav>
                    ) : null}
                </TopNavLinksWrapper>
            </ContentContainer>
        </Wrap>
    );
};

TopHeader.propTypes = {
    languages: PropTypes.arrayOf(
        PropTypes.shape({
            code: PropTypes.string,
            translated_name: PropTypes.string,
        }),
    ),
    locationSiteLinks: PropTypes.shape({
        currentLocation: PropTypes.string.isRequired,
        additionalSiteLocations: PropTypes.arrayOf(
            PropTypes.shape({
                linkLabel: PropTypes.string.isRequired,
                linkUrl: PropTypes.string.isRequired,
            }),
        ),
    }),
    topNav: PropTypes.arrayOf(
        PropTypes.shape({
            linkLabel: PropTypes.string.isRequired,
            linkUrl: PropTypes.string.isRequired,
            subNavigationItems: PropTypes.arrayOf(
                PropTypes.shape({
                    linkUrl: PropTypes.string.isRequired,
                    linkLabel: PropTypes.string.isRequired,
                }),
            ),
        }),
    ),
    openTopSubnav: PropTypes.string,
    dispatch: PropTypes.func.isRequired,
    mobileMenuIsOpen: PropTypes.bool.isRequired,
};

export default TopHeader;
